import { FadeUp, MaskedLine, SectionTag, Stars } from "../components/Reveal";
import { CLINIC, REMEDY_GROUPS, TESTIMONIALS, GOOGLE_REVIEW } from "../data/clinic";
import { Phone, Check, Star, ArrowRight, ArrowUpRight, Quote } from "lucide-react";

const Remedy = () => (
    <main data-testid="remedy-page" className="pt-32">
        <section className="relative mx-auto max-w-7xl overflow-hidden px-4 pb-16 sm:px-8 lg:px-16 lg:pb-24">
            <div className="noise-overlay pointer-events-none absolute inset-0 opacity-20" />
            <SectionTag className="relative mb-8">Our Remedy</SectionTag>
            <h1
                data-testid="remedy-hero-title"
                className="font-display relative max-w-4xl text-4xl font-light leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
            >
                <MaskedLine>Every eye tells</MaskedLine>
                <MaskedLine delay={0.12}>
                    a <em className="text-emerald-400">different</em> story.
                </MaskedLine>
            </h1>
            <FadeUp delay={0.3} className="relative mt-8 max-w-2xl">
                <p className="text-base font-light leading-relaxed text-[#a3b8a8] sm:text-lg">
                    A complete range of eye-care services under one roof — from routine exams to
                    specialised clinics, so you always know exactly where to turn.
                </p>
            </FadeUp>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-8 lg:px-16 lg:pb-32">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {REMEDY_GROUPS.map((g, i) => (
                    <FadeUp key={g.testId} delay={(i % 3) * 0.08}>
                        <div
                            data-testid={g.testId}
                            className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-emerald-500/15 bg-[#101f15]/70 p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-emerald-400/50 hover:bg-[#14261a] hover:shadow-[0_16px_48px_rgba(34,197,94,0.14)]"
                        >
                            <span className="font-display absolute right-6 top-5 text-4xl font-light text-emerald-500/15 transition-colors duration-500 group-hover:text-emerald-500/30">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl border border-emerald-500/25 bg-emerald-950/60 transition-all duration-500 group-hover:border-emerald-400/60 group-hover:shadow-[0_0_24px_rgba(34,197,94,0.25)]">
                                <g.icon className="h-6 w-6 text-emerald-400" />
                            </div>
                            <h3 className="font-display text-2xl font-normal tracking-tight">{g.title}</h3>
                            <ul className="mt-4 flex-1 space-y-2.5">
                                {g.bullets.map((b) => (
                                    <li
                                        key={b}
                                        className="flex items-start gap-2.5 text-sm font-light leading-relaxed text-[#a3b8a8]"
                                    >
                                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
                                        {b}
                                    </li>
                                ))}
                            </ul>
                            <a
                                href={CLINIC.phoneHref}
                                className="mt-6 flex w-fit items-center gap-2 text-sm font-medium text-emerald-300 transition-colors duration-300 hover:text-emerald-200"
                            >
                                Call to book
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </a>
                        </div>
                    </FadeUp>
                ))}
            </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
            <FadeUp>
                <a
                    href={CLINIC.googleProfile}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="google-review-highlight-card"
                    className="mb-14 flex flex-wrap items-center justify-between gap-6 rounded-3xl border border-emerald-500/20 bg-gradient-to-r from-[#101f15] to-[#0b150e] p-8 transition-all duration-500 hover:border-emerald-400/50 hover:shadow-[0_0_36px_rgba(34,197,94,0.12)] lg:p-10"
                >
                    <div className="flex items-center gap-5">
                        <Star className="h-10 w-10 fill-amber-400 text-amber-400" />
                        <div>
                            <p className="font-display text-4xl font-light text-emerald-50">
                                {CLINIC.rating} <span className="text-xl text-[#687f6e]">/ 5</span>
                            </p>
                            <Stars className="mt-1 h-4 w-4" />
                        </div>
                    </div>
                    <p className="max-w-md text-sm font-light leading-relaxed text-[#a3b8a8]">
                        Rated on Google — for accurate diagnoses,
                        honest advice and genuinely patient-centered care.
                    </p>
                    <span className="flex items-center gap-2 text-xs font-mono tracking-[0.2em] uppercase text-emerald-400">
                        Read all reviews
                        <ArrowUpRight className="h-4 w-4" />
                    </span>
                </a>
            </FadeUp>

            <FadeUp delay={0.1}>
                <figure
                    data-testid="google-featured-review"
                    className="relative mb-10 overflow-hidden rounded-3xl border border-amber-400/25 bg-gradient-to-br from-[#14261a] to-[#0b150e] p-8 lg:p-12"
                >
                    <Quote className="absolute right-8 top-8 h-10 w-10 text-amber-400/20" />
                    <Stars className="h-4 w-4" />
                    <blockquote className="font-display mt-5 max-w-3xl text-2xl font-light italic leading-relaxed text-emerald-50 sm:text-3xl">
                        “{GOOGLE_REVIEW.quote}”
                    </blockquote>
                    <figcaption className="mt-6 flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-950/60 font-semibold text-emerald-300">
                            G
                        </span>
                        <span className="text-xs font-mono tracking-[0.2em] uppercase text-[#a3b8a8]">
                            {GOOGLE_REVIEW.source}
                        </span>
                    </figcaption>
                </figure>
            </FadeUp>

            <div data-testid="remedy-testimonial-list" className="grid gap-6 md:grid-cols-2">
                {TESTIMONIALS.map((t, i) => (
                    <FadeUp key={t.testId} delay={(i % 2) * 0.1}>
                        <figure
                            data-testid={t.testId}
                            className="flex h-full flex-col justify-between rounded-3xl border border-emerald-500/15 bg-[#101f15]/70 p-8 transition-all duration-500 hover:border-emerald-400/40 lg:p-10"
                        >
                            <blockquote className="font-display text-xl font-light italic leading-relaxed text-emerald-50/90">
                                “{t.quote}”
                            </blockquote>
                            <figcaption className="mt-8 flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-semibold text-emerald-50">{t.name}</p>
                                    <p className="text-xs text-[#687f6e]">
                                        {t.area} · {t.service}
                                    </p>
                                </div>
                                <Stars className="h-3.5 w-3.5" />
                            </figcaption>
                        </figure>
                    </FadeUp>
                ))}
            </div>
        </section>
    </main>
);

export default Remedy;
